import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLocale } from "../i18n";
import { fetchRegions, fetchRegion } from "../lib/api";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import SEO, { buildBreadcrumb } from "../components/SEO";

export function RegionsList() {
  const { locale, t } = useLocale();
  const [regions, setRegions] = useState([]);
  useEffect(() => { fetchRegions().then(setRegions).catch(() => {}); }, []);
  const nameKey = locale === "ro" ? "name_ro" : "name_en";
  const taglineKey = locale === "ro" ? "tagline_ro" : "tagline_en";

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="regions-list-page">
      <SEO
        title={locale === "ro" ? "Nouă regiuni culturale ale României" : "Nine cultural regions of Romania"}
        description={locale === "ro"
          ? "Explorează cele nouă regiuni culturale ale României — Transilvania, Bucovina, Maramureș, Banat, Muntenia, Oltenia, Moldova, Dobrogea, Crișana."
          : "Explore Romania's nine cultural regions — Transylvania, Bucovina, Maramureș, Banat, Muntenia, Oltenia, Moldova, Dobrogea, Crișana."}
        path="/regions"
        jsonLd={[buildBreadcrumb([
          { name: locale === "ro" ? "Acasă" : "Home", path: "/" },
          { name: locale === "ro" ? "Destinații" : "Destinations", path: "/regions" },
        ])]}
      />
      <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-4">
        / {locale === "ro" ? "LUMI" : "WORLDS"} · 09
      </p>
      <h1 className="font-editorial text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight text-[var(--wr-ink)] mb-12">
        {t("regions.heading")}
      </h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {regions.map((r, i) => (
          <Link
            key={r.slug}
            to={`/regions/${r.slug}`}
            data-testid={`region-tile-${r.slug}`}
            className="wr-img-mask group block relative aspect-[4/5] border border-[var(--wr-border)] hover:border-[var(--wr-ink)] transition-colors duration-500"
          >
            <img src={r.image} alt={r[nameKey]} className="w-full h-full object-cover" loading="lazy" decoding="async" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute inset-0 p-7 flex flex-col justify-between text-white">
              <p className="font-mono-wr text-[10px] uppercase tracking-widest text-white/85">
                / {String(i + 1).padStart(2, "0")}
              </p>
              <div>
                <h3 className="font-editorial text-4xl leading-none tracking-tight">{r[nameKey]}</h3>
                <p className="mt-3 text-[14px] text-white/85 leading-snug">{r[taglineKey]}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-[11px] font-mono-wr uppercase tracking-widest">
                  {t("regions.open")} <ArrowUpRight size={14} />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function RegionDetail() {
  const { slug } = useParams();
  const { locale, t } = useLocale();
  const [region, setRegion] = useState(null);
  useEffect(() => { fetchRegion(slug).then(setRegion).catch(() => {}); }, [slug]);
  if (!region) return <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 text-[var(--wr-ink-2)]">{t("common.loading")}</div>;

  const nameKey = locale === "ro" ? "name_ro" : "name_en";
  const taglineKey = locale === "ro" ? "tagline_ro" : "tagline_en";
  const descKey = locale === "ro" ? "description_ro" : "description_en";

  const touristLd = {
    "@context": "https://schema.org",
    "@type": "TouristDestination",
    name: region[nameKey],
    description: region[descKey],
    url: `https://wowromania.com/regions/${slug}`,
    image: region.image,
    containedInPlace: { "@type": "Country", name: "Romania" },
    touristType: (region.highlights || []),
    inLanguage: locale,
  };

  return (
    <article data-testid={`region-detail-${slug}`}>
      <SEO
        title={region[nameKey]}
        description={region[taglineKey]}
        path={`/regions/${slug}`}
        image={region.image}
        type="article"
        jsonLd={[
          touristLd,
          buildBreadcrumb([
            { name: locale === "ro" ? "Acasă" : "Home", path: "/" },
            { name: locale === "ro" ? "Destinații" : "Destinations", path: "/regions" },
            { name: region[nameKey], path: `/regions/${slug}` },
          ]),
        ]}
      />
      <div className="relative h-[70vh] overflow-hidden">
        <img src={region.image} alt={region[nameKey]} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: region.image_position }} fetchPriority="high" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-16 text-white">
          <Link to="/regions" className="inline-flex items-center gap-2 font-mono-wr text-[11px] uppercase tracking-widest mb-6 hover:text-[var(--wr-red)] transition-colors" data-testid="back-to-regions">
            <ArrowLeft size={14} /> {locale === "ro" ? "Toate lumile" : "All worlds"}
          </Link>
          <h1 className="font-editorial text-6xl sm:text-8xl leading-[0.9] tracking-tight">{region[nameKey]}</h1>
          <p className="mt-4 max-w-2xl text-[18px] text-white/90">{region[taglineKey]}</p>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <p className="font-editorial text-2xl sm:text-3xl leading-relaxed text-[var(--wr-ink)]">
            {region[descKey]}
          </p>
        </div>
        <aside className="lg:col-span-4 border-t lg:border-t-0 lg:border-l border-[var(--wr-border)] pt-8 lg:pt-0 lg:pl-8">
          <p className="font-mono-wr text-[10px] uppercase tracking-[0.2em] text-[var(--wr-ink-2)] mb-4">
            {t("regions.highlights")}
          </p>
          <ul className="space-y-3">
            {region.highlights.map((h, i) => (
              <li key={i} className="flex items-baseline gap-3 text-[16px] text-[var(--wr-ink)] border-b border-[var(--wr-border)] pb-3">
                <span className="font-mono-wr text-[10px] text-[var(--wr-red)]">0{i + 1}</span>
                {h}
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </article>
  );
}
